// Background music. Opus is served as the primary source (smaller file,
// same/better quality) with an mp3 fallback for browsers that can't decode
// it (Safari). Most browsers block unmuted autoplay unless the page has
// already earned a "media engagement" gesture, so we always attempt it
// immediately on load and just fall silently back to paused-until-gesture
// when that's rejected - retryAutoplay() is what the mute button and PLAY
// button call to satisfy that gesture requirement afterwards.
import { isMuted, setMuted, onMuteChange } from "./muteState.js";

const ICON_ON =
  '<svg viewBox="0 0 24 24" width="20" height="20"><path d="M4 9v6h4l5 5V4L8 9H4z" fill="currentColor"/><path d="M16.5 8.5a5 5 0 0 1 0 7" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/><path d="M19 6a9 9 0 0 1 0 12" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>';
const ICON_OFF =
  '<svg viewBox="0 0 24 24" width="20" height="20"><path d="M4 9v6h4l5 5V4L8 9H4z" fill="currentColor"/><line x1="16" y1="9" x2="22" y2="15" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/><line x1="22" y1="9" x2="16" y2="15" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>';

export function createMusicController(button) {
  const audio = new Audio();
  audio.loop = true;
  audio.volume = 0.4;
  audio.preload = "auto";
  audio.src = audio.canPlayType('audio/ogg; codecs="opus"')
    ? "/sfx/music/soundtrack_time_it_takes.opus"
    : "/sfx/music/soundtrack_time_it_takes.mp3";

  audio.muted = isMuted();
  let playbackStarted = false;

  function render() {
    if (!button) return;
    const muted = isMuted();
    button.innerHTML = muted ? ICON_OFF : ICON_ON;
    button.setAttribute("aria-label", muted ? "Unmute music" : "Mute music");
    button.classList.toggle("muted", muted);
  }

  function retryAutoplay() {
    if (playbackStarted) return;
    audio.play().then(
      () => { playbackStarted = true; },
      () => {} // still blocked - next gesture (mute click, PLAY click) will try again
    );
  }

  // Shared across every audio source (music + SFX) so one mute button
  // silences all of it - see muteState.js.
  onMuteChange((muted) => {
    audio.muted = muted;
    render();
    if (!muted) retryAutoplay();
  });

  // Tab switched away / phone locked - pause playback rather than let it
  // keep running unheard, and pick back up where it left off on return.
  document.addEventListener("visibilitychange", () => {
    if (document.hidden) {
      audio.pause();
    } else if (playbackStarted) {
      audio.play().catch(() => {});
    }
  });

  // Before playback has actually started (autoplay was blocked), the button
  // click is the gesture that unlocks it - treat it as "turn sound on"
  // rather than a literal mute/unmute toggle, since nothing is audible yet
  // regardless of what `isMuted()` says.
  button?.addEventListener("click", () => setMuted(playbackStarted ? !isMuted() : false));

  render();
  retryAutoplay();

  return { retryAutoplay };
}
